import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import {useTable} from 'react-table';
import axios from 'axios';

import WeekSelector from './WeekSelector';
import {makeData} from '../../../utils/makeData'; // serverPromise

// Auto-detect backend URL: localhost for dev, Cloudflare tunnel for production
const HOST_PORT = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:3003'
  : 'https://api.blockhivetechnologies.com';

// Animated 3-block sequencer indicator
const SEQ_COLORS = [
  { bg: 'rgba(0,229,122,0.15)', border: 'rgba(0,229,122,0.5)' },
  { bg: 'rgba(245,194,0,0.12)', border: 'rgba(245,194,0,0.5)' },
  { bg: 'rgba(232,0,58,0.12)',  border: 'rgba(232,0,58,0.5)'  },
];

function generateSeqBuffer(len) {
  const phases = ['inc', 'hold', 'dec'];
  const buf = [];
  let phaseIdx = Math.floor(Math.random() * 3);
  let runLeft = 1 + Math.floor(Math.random() * 2);
  for (let i = 0; i < len; i++) {
    if (runLeft <= 0) {
      phaseIdx = (phaseIdx + 1) % 3;
      runLeft = phaseIdx === 1 ? 1 : 1 + Math.floor(Math.random() * 2);
    }
    buf.push(phaseIdx);
    runLeft--;
  }
  return buf;
}

function SeqMiniAnim() {
  const [buf]    = useState(() => generateSeqBuffer(60));
  const [ptr, setPtr] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPtr(p => (p + 1) % 60), 1100);
    return () => clearInterval(t);
  }, []);
  const blocks = [buf[ptr % 60], buf[(ptr + 1) % 60], buf[(ptr + 2) % 60]];
  return (
    <span style={{ display: 'inline-flex', gap: '2px', marginLeft: '5px', verticalAlign: 'middle' }}>
      {blocks.map((ci, i) => (
        <span key={i} style={{
          width: '8px', height: '12px', borderRadius: '1px',
          background: SEQ_COLORS[ci].bg,
          border: `1px solid ${SEQ_COLORS[ci].border}`,
          transition: 'background 0.3s, border-color 0.3s',
          display: 'inline-block',
        }} />
      ))}
    </span>
  );
}

// Trading signals are calculated server-side to protect proprietary algorithms

// Get current week dates
const getCurrentWeekDates = () => {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Get Monday of current week

  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday);
  monday.setHours(3, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 0, 0, 0);

  return {
    startDate: monday.toISOString().split('.')[0] + 'Z',
    endDate: sunday.toISOString().split('.')[0] + 'Z',
    startDate4hr: new Date(monday.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString().split('.')[0] + 'Z'
  };
};

const currentWeek = getCurrentWeekDates();
let callString_main = HOST_PORT + `/btc_today`; // Fetches recent hourly data with RSI
let callString_4_hours = HOST_PORT + `/btc_today`; // Same endpoint provides both hourly and 4hr data

// Create a default prop getter
const defaultPropGetter = () => ({})

function Table(
  {
    columns,
    data,
    getHeaderProps = defaultPropGetter,
    getColumnProps = defaultPropGetter,
    getRowProps = defaultPropGetter,
    getCellProps = defaultPropGetter,
  }
) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
    }
  )


  // Render the UI for the Calendar WeekSelector and the Table
  return (
    <>
      <table {...getTableProps()}>
        <thead>
        {headerGroups.map(headerGroup => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map(column => (
              <th {...column.getHeaderProps()} style={column.id === 'sqncr-group' ? { overflow: 'visible' } : {}}>{column.render('Header')}</th>
            ))}
          </tr>
        ))}
        </thead>

        <tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row)
          return (
            <tr key={row.id}>
              {row.cells.map(cell => {
                const { key: cellKey, ...cellProps } = cell.getCellProps([
                  {
                    className: cell.column.className,
                    style: cell.column.style,
                  },
                  getColumnProps(cell.column),
                  row.index >= 1 ? getCellProps(cell) : {},
                ])
                return (
                  <td key={cellKey} {...cellProps}>
                    {cell.render('Cell')}
                  </td>
                )
              })}
            </tr>
          )
        })}
        </tbody>

      </table>
    </>
  )
}

function XYR() {
  const columns = React.useMemo(
    () => [
      {
        Header: () => (
          <span className="sqnr-header" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', marginRight: '2px' }}>
            SQNCR
            <SeqMiniAnim />
          </span>
        ),
        id: 'sqncr-group',
        columns: [
          { Header: '', accessor: 'rValue' },
        ],
      },
      {
        Header: 'Mon',
        columns: [
          { Header: 'ON', accessor: 'overnightMonday' },
          { Header: 'O', accessor: 'openMonday' },
          { Header: 'C', accessor: 'closeMonday' },
        ],
      },
      {
        Header: 'Tue',
        columns: [
          { Header: 'ON', accessor: 'overnightTuesday' },
          { Header: 'O', accessor: 'openTuesday' },
          { Header: 'C', accessor: 'closeTuesday' },
        ],
      },
      {
        Header: 'Wed',
        columns: [
          { Header: 'ON', accessor: 'overnightWednesday' },
          { Header: 'O', accessor: 'openWednesday' },
          { Header: 'C', accessor: 'closeWednesday' },
        ],
      },
      {
        Header: 'Thu',
        columns: [
          { Header: 'ON', accessor: 'overnightThursday' },
          { Header: 'O', accessor: 'openThursday' },
          { Header: 'C', accessor: 'closeThursday' },
        ],
      },
      {
        Header: 'Fri',
        columns: [
          { Header: 'ON', accessor: 'overnightFriday' },
          { Header: 'O', accessor: 'openFriday' },
          { Header: 'C', accessor: 'closeFriday' },
        ],
      },
      {
        Header: 'Sat',
        columns: [
          { Header: 'ON', accessor: 'overnightSaturday' },
          { Header: 'O', accessor: 'openSaturday' },
          { Header: 'C', accessor: 'closeSaturday' },
        ],
      },
      {
        Header: 'Sun',
        columns: [
          { Header: 'ON', accessor: 'overnightSunday' },
          { Header: 'O', accessor: 'openSunday' },
          { Header: 'C', accessor: 'closeSunday' },
        ],
      },
    ],
    []
  )

  const [values, setValues] = useState([]);
  const [prices, setPrices] = useState([]);
  const [every4hours_r, setEvery4Hours] = useState([]);
  const [modes4hr, setModes4hr] = useState([]);
  const [holdStates4hr, setHoldStates4hr] = useState([]);
  const [modesDaily, setModesDaily] = useState([]);

  const [updated_every4_hrs, set4Hours] = useState([]);
  const [updated_hourly, setHourly] = useState([]);
  const [isCurrentWeek, setIsCurrentWeek] = useState(true);
  const [debugInfo, setDebugInfo] = useState(null);
  const [sessionTick, setSessionTick] = useState(0);
  const [etClock, setEtClock] = useState('');

  // Fire sessionTick at exact session boundaries (3am, 9:30am, 4pm ET) + 30s heartbeat fallback
  useEffect(() => {
    let boundaryTimeout = null;

    function msToNextBoundary() {
      const now = new Date();
      const noonUTC = new Date(now); noonUTC.setUTCHours(12, 0, 0, 0);
      const nyHourAtNoon = parseInt(
        new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false }).format(noonUTC), 10
      );
      const etOffsetH = 12 - nyHourAtNoon;
      const etDate = new Date(now.getTime() - etOffsetH * 3_600_000);
      const etH = etDate.getUTCHours();
      const etMin = etDate.getUTCMinutes();
      const etSec = etDate.getUTCSeconds();
      const etMs  = etDate.getUTCMilliseconds();
      const boundaries = [3 * 60, 9 * 60 + 30, 16 * 60]; // minutes since midnight ET
      const nowMin = etH * 60 + etMin;
      const nowSec = nowMin * 60 + etSec;
      let minDelta = Infinity;
      for (const bMin of boundaries) {
        let delta = bMin * 60 - nowSec;
        if (delta <= 0) delta += 24 * 3600;
        if (delta < minDelta) minDelta = delta;
      }
      return minDelta * 1000 - etMs + 500; // +500ms cushion past the boundary
    }

    function scheduleBoundary() {
      const delay = msToNextBoundary();
      boundaryTimeout = setTimeout(() => {
        setSessionTick(t => t + 1);
        set4Hours(Date.now());
        scheduleBoundary();
      }, delay);
    }

    scheduleBoundary();

    // 30s heartbeat so the gate stays accurate if component is left open for days
    const heartbeat = setInterval(() => setSessionTick(t => t + 1), 30 * 1000);

    const clockTimer = setInterval(() => {
      const n = new Date();
      const p = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: true, weekday: 'short'
      }).format(n);
      setEtClock(p + ' ET');
    }, 1000);

    return () => {
      clearTimeout(boundaryTimeout);
      clearInterval(heartbeat);
      clearInterval(clockTimer);
    };
  }, []);

  // Calculate maxDayIndex: 0=Mon, 6=Sun. null = show all (historical week)
  // Calculate maxSessionIndex by ET session boundaries: ON shows after 3am, O after 9:30am, C after 4pm
  const { maxDayIndex, maxSessionIndex } = React.useMemo(() => {
    if (!isCurrentWeek) return { maxDayIndex: null, maxSessionIndex: null };

    const now = new Date();

    // DST-aware ET offset: probe noon UTC through America/New_York
    // (same pattern as binance_client/index.js — avoids formatToParts quirks)
    const noonUTC = new Date(now);
    noonUTC.setUTCHours(12, 0, 0, 0);
    const nyHourAtNoon = parseInt(
      new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false }).format(noonUTC),
      10
    );
    const etOffsetH = 12 - nyHourAtNoon; // 4 = EDT, 5 = EST

    // Shift UTC timestamp to ET and read as UTC components to get ET h/m/day
    const etDate   = new Date(now.getTime() - etOffsetH * 3_600_000);
    const etH      = etDate.getUTCHours();
    const etMin    = etDate.getUTCMinutes();
    const etUTCDay = etDate.getUTCDay(); // 0=Sun, 1=Mon … 6=Sat
    const estTime  = etH + etMin / 60;
    const dayIndex = etUTCDay === 0 ? 6 : etUTCDay - 1; // 0=Mon … 6=Sun

    if (estTime < 3) {
      // Before 3am ET: previous day's Close is the last completed session
      return { maxDayIndex: dayIndex > 0 ? dayIndex - 1 : 6, maxSessionIndex: 2 };
    } else if (estTime < 9.5) {
      // 3am – 9:30am ET: today's Overnight just became available
      return { maxDayIndex: dayIndex, maxSessionIndex: 0 };
    } else if (estTime < 16) {
      // 9:30am – 4pm ET: today's Open just became available
      return { maxDayIndex: dayIndex, maxSessionIndex: 1 };
    } else {
      // After 4pm ET: all three sessions complete
      return { maxDayIndex: dayIndex, maxSessionIndex: 2 };
    }
  }, [isCurrentWeek, sessionTick]);

  // Log session values for debugging
  React.useEffect(() => {
    console.log("=== SEQUENCER STATE ===");
    console.log(`isCurrentWeek: ${isCurrentWeek}`);
    console.log(`maxDayIndex: ${maxDayIndex} (0=Mon, 6=Sun, null=historical)`);
    console.log(`maxSessionIndex: ${maxSessionIndex} (0=N, 1=O, 2=C, null=historical)`);
    console.log("=====================");
  }, [isCurrentWeek, maxDayIndex, maxSessionIndex]);

  function changeWeek(newWeekRange) {
    setIsCurrentWeek(false);
    callString_4_hours = HOST_PORT + "/btc_4_hour_dates_times_between?" + newWeekRange;
    callString_main = HOST_PORT + "/btc_dates_times_between?" + newWeekRange;
    console.log("callString_4_hours = " + callString_4_hours);

    set4Hours(callString_4_hours);
    setHourly(callString_main);
  }

  useEffect(() => {
    const fetchData = async () => {
      console.log('[XYR] fetchData starting..., callString:', callString_main);
      try {
        const result = await axios(
          callString_main,
        );
        console.log('[XYR] API response received, data keys:', Object.keys(result.data));

      // Extract 4H data for current calendar week (Monday-Sunday) by date matching
      // This ensures we get 4H data for the actual week days, not just "last 42 bars"

      const now = new Date();
      const currentDay = now.getUTCDay(); // 0=Sun, 1=Mon, ..., 6=Sat (UTC)
      const daysFromMonday = currentDay === 0 ? 6 : currentDay - 1; // Mon=0, Sun=6

      // Calculate Monday of current week (UTC to match Binance timestamps)
      const monday = new Date(now);
      monday.setUTCDate(now.getUTCDate() - daysFromMonday);
      monday.setUTCHours(0, 0, 0, 0);

      // Get all candles and 4H data
      const all_candles = result.data.ticker || [];
      const all_r_values_raw = result.data.custom_list_10_30 || [];  // 4H RSI with (10,30) params
      const all_modes = result.data.modes_4hr_10_30 || [];  // 4H modes with (10,30) params
      const all_hold_states = result.data.hold_states_4hr_10_30 || [];  // Hold states for uncertainty display

      // Calculate warmup offset (candles without R values at the start)
      const warmupOffset = all_candles.length - all_r_values_raw.length;

      // Build 42-bar arrays (6 bars/day × 7 days) for current week
      const px = [];
      const r_values = [];
      const modes = [];
      const hold_states = [];

      // For each day of the week (Mon-Sun)
      for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
        const targetDate = new Date(monday);
        targetDate.setDate(monday.getDate() + dayOffset);
        const dateKey = targetDate.toISOString().split('T')[0];

        // Find all 4H candles from this date
        const dayCandles = [];
        all_candles.forEach((candle, idx) => {
          const candleDate = new Date(candle.time).toISOString().split('T')[0];
          if (candleDate === dateKey) {
            dayCandles.push({ candle, idx });
          }
        });

        // Extract 6 4H bars for this day (or use null if not available)
        // We need bars at: 0:00, 4:00, 8:00, 12:00, 16:00, 20:00
        for (let barOffset = 0; barOffset < 6; barOffset++) {
          if (barOffset < dayCandles.length) {
            const { candle, idx } = dayCandles[barOffset];
            px.push(Math.round(candle.close));

            // Find corresponding R value (accounting for warmup offset)
            // Candle index X maps to R value index (X - warmupOffset)
            const rValueIdx = idx - warmupOffset;

            if (rValueIdx >= 0 && rValueIdx < all_r_values_raw.length && all_r_values_raw[rValueIdx] && all_r_values_raw[rValueIdx].rsi != null) {
              r_values.push(parseFloat(((all_r_values_raw[rValueIdx].rsi / 50) - 1).toFixed(2)));
            } else {
              r_values.push(null);
            }

            // Mode also uses warmup offset (aligned with R values, not candles)
            const modeIdx = idx - warmupOffset;
            modes.push(modeIdx >= 0 && modeIdx < all_modes.length ? all_modes[modeIdx] : null);

            // Hold state also uses warmup offset (aligned with R values and modes)
            const holdIdx = idx - warmupOffset;
            hold_states.push(holdIdx >= 0 && holdIdx < all_hold_states.length ? all_hold_states[holdIdx] : 0);
          } else {
            // Future bar - use null
            px.push(null);
            r_values.push(null);
            modes.push(null);
            hold_states.push(0);
          }
        }
      }

      // Debug logging for Monday data
      console.log('[XYR Debug] Monday data extraction:');
      console.log('  px[0] (ON):', px[0], '  px[2] (O):', px[2], '  px[4] (C):', px[4]);
      console.log('  r_values[0] (ON):', r_values[0], '  r_values[2] (O):', r_values[2], '  r_values[4] (C):', r_values[4]);
      console.log('  modes[0] (ON):', modes[0], '  modes[2] (O):', modes[2], '  modes[4] (C):', modes[4]);

      // Debug logging for Tuesday data
      console.log('[XYR Debug] Tuesday data extraction:');
      console.log('  px[3] (ON):', px[3], '  px[5] (O):', px[5], '  px[7] (C):', px[7]);
      console.log('  r_values[3] (ON):', r_values[3], '  r_values[5] (O):', r_values[5], '  r_values[7] (C):', r_values[7]);
      console.log('  modes[3] (ON):', modes[3], '  modes[5] (O):', modes[5], '  modes[7] (C):', modes[7]);

      console.log('  Total candles found:', all_candles.length);
      console.log('  Total R values:', all_r_values_raw.length);
      console.log('  Warmup offset:', warmupOffset);

      setValues(r_values);
      setPrices(px);
      setModes4hr(modes);
      setHoldStates4hr(hold_states);
      } catch (error) {
        console.error('[XYR] Error fetching data:', error);
      }
    };
    fetchData();

  }, [updated_hourly]);

  useEffect(() => {

    const fetchData = async () => {
      const result_4_hrs = await axios(
        callString_4_hours,
      );

      // Extract daily values for current calendar week (Monday-Sunday) by date matching
      // This ensures we get daily data for the actual week days, not just "last 42 bars"

      const now = new Date();
      const currentDay = now.getUTCDay(); // 0=Sun, 1=Mon, ..., 6=Sat (UTC)
      const daysFromMonday = currentDay === 0 ? 6 : currentDay - 1; // Mon=0, Sun=6

      // Calculate Monday of current week (UTC to match Binance timestamps)
      const monday = new Date(now);
      monday.setUTCDate(now.getUTCDate() - daysFromMonday);
      monday.setUTCHours(0, 0, 0, 0);

      // Get all candles and daily data
      // Note: modes_daily is now aligned to 4H candles by the backend (same length as all_candles)
      const all_candles = result_4_hrs.data.ticker || [];
      const all_daily_rsi = result_4_hrs.data.custom_rsi_list_4hr || [];
      const all_daily_modes = result_4_hrs.data.modes_daily || [];
      const all_daily_hold = result_4_hrs.data.hold_states_daily || [];

      // Extract daily values for each day of current week (Mon-Sun)
      const r_4_hrs = [];
      const daily_modes = [];

      for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
        const targetDate = new Date(monday);
        targetDate.setDate(monday.getDate() + dayOffset);
        const dateKey = targetDate.toISOString().split('T')[0];

        // Check if this day has closed (after 4 PM ET = US market close)
        // DST-aware: noon UTC in America/New_York tells us the ET offset
        const noonUTC = new Date(`${dateKey}T12:00:00.000Z`);
        const etHourAtNoon = parseInt(new Intl.DateTimeFormat('en-US', {
          timeZone: 'America/New_York', hour: 'numeric', hour12: false
        }).format(noonUTC));
        const etOffsetHours = 12 - etHourAtNoon; // 4 = EDT, 5 = EST
        const marketClose = new Date(`${dateKey}T${String(16 + etOffsetHours).padStart(2,'0')}:00:00.000Z`);
        const dayHasClosed = now >= marketClose;

        // The daily close is the 4 PM ET bar = the 16:00-UTC-open 4H bar (closes 20:00 UTC = 4 PM
        // EDT; also the last 4H close before 4 PM EST). The backend groups the daily by THIS bar,
        // so read it for the day's committed close/mode. (The 20:00-UTC bar now belongs to the NEXT
        // trading day — reading it showed every day its successor's mode, so Mon+Tue both read HOLD.)
        let candleIndex = -1;
        for (let ci = all_candles.length - 1; ci >= 0; ci--) {
          const ct = new Date(all_candles[ci].time);
          if (ct.toISOString().split('T')[0] === dateKey && ct.getUTCHours() === 16) {
            candleIndex = ci;
            break;
          }
        }

        if (candleIndex !== -1 && dayHasClosed) {
          // Extract daily RSI value only if day has closed (complete daily bar)
          const dailyRSI = all_daily_rsi[candleIndex];
          r_4_hrs.push(dailyRSI && dailyRSI.rsi != null ?
            parseFloat(((dailyRSI.rsi / 50) - 1).toFixed(2)) : null);

          // Extract daily mode — if direction is uncertain, use 'hold' (yellow) even if buyMode persists
          const rawMode = all_daily_modes[candleIndex] || null;
          const isHold = all_daily_hold[candleIndex] === 1;
          daily_modes.push(isHold ? 'hold' : rawMode);
        } else {
          // Future day or current incomplete day - use null
          r_4_hrs.push(null);
          daily_modes.push(null);
        }
      }

      setEvery4Hours(r_4_hrs);
      setModesDaily(daily_modes);
    };
    fetchData();

  }, [updated_every4_hrs]);

  // Auto-refresh data every 30 seconds + version check
  useEffect(() => {
    const BUNDLE_VERSION = '20260609-2';

    const checkVersionAndReload = async () => {
      try {
        const res = await fetch('/version.json?_=' + Date.now());
        const data = await res.json();
        if (data.v && data.v !== BUNDLE_VERSION) {
          console.log('[XYR] New version detected, reloading...');
          window.location.reload();
        }
      } catch (_) {}
    };

    checkVersionAndReload(); // check immediately on mount

    // Fetch live r_current for debug indicator
    const updateDebug = async () => {
      try {
        const HOST = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          ? 'http://localhost:3003' : 'https://api.blockhivetechnologies.com';
        const res = await fetch(`${HOST}/btc_today?_=${Date.now()}`);
        const data = await res.json();
        const rc = data.r_current;
        if (rc) setDebugInfo({ r4h: rc.r_4h, rDaily: rc.r_daily, m4h: rc.mode_4h, mDaily: rc.mode_daily });
      } catch (_) {}
    };
    updateDebug();

    const refreshInterval = setInterval(() => {
      console.log('[XYR Auto-refresh] Reloading data...', new Date().toLocaleTimeString());
      set4Hours(Date.now()); // Trigger 4hr data fetch
      setHourly(Date.now());  // Trigger hourly data fetch
      checkVersionAndReload();
    }, 30000); // 30 seconds

    return () => clearInterval(refreshInterval);
  }, []);

  const rColor = (v, mode) => mode === 'sell' ? '#ff6b6b' : mode === 'buy' ? '#51cf66' : '#f7c948';

  const sessionLabel = maxSessionIndex === 0 ? 'ON' : maxSessionIndex === 1 ? 'O' : maxSessionIndex === 2 ? 'C' : '—';
  const dayNames = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

  return (
    <div className={'xyr'}>
      <div style={{
        display:'flex', gap:16, padding:'3px 10px', background:'#080808',
        borderBottom:'1px solid #111', fontSize:9, fontFamily:'monospace',
        letterSpacing:'0.5px', alignItems:'center', color:'#555',
      }}>
        <span style={{color:'#3a3a3a'}}>NY</span>
        <span style={{color:'#4a4a4a', minWidth:160}}>{etClock || '—'}</span>
        <span style={{color:'#2a2a2a'}}>|</span>
        <span>day <span style={{color:'#606060'}}>{maxDayIndex != null ? dayNames[maxDayIndex] : '—'}</span></span>
        <span>sess <span style={{color:'#606060'}}>{sessionLabel}</span></span>
        <span style={{color:'#2a2a2a'}}>tick {sessionTick}</span>
        {debugInfo && <>
          <span style={{color:'#2a2a2a'}}>|</span>
          <span style={{color:'#444'}}>LIVE</span>
          <span style={{color: rColor(debugInfo.r4h, debugInfo.m4h), fontWeight:700}}>
            R4H {debugInfo.r4h >= 0 ? '+' : ''}{debugInfo.r4h.toFixed(3)} [{debugInfo.m4h?.toUpperCase()}]
          </span>
          <span style={{color: rColor(debugInfo.rDaily, debugInfo.mDaily), fontWeight:700}}>
            RDLY {debugInfo.rDaily >= 0 ? '+' : ''}{debugInfo.rDaily.toFixed(3)} [{debugInfo.mDaily?.toUpperCase()}]
          </span>
        </>}
        <span style={{color:'#222', marginLeft:'auto', fontSize:8}}>v20260611</span>
      </div>
      {/* <WeekSelector weekChangeHandler={changeWeek}/> */}
      <div className={"xyr-tableContainer"}>
        <Table
          className={"xyr-table"}
          columns={columns}
          data={makeData(values, prices, every4hours_r, modes4hr, holdStates4hr, modesDaily, maxDayIndex, maxSessionIndex, Number(3))}
          getHeaderProps={column => ({
            onClick: () => alert('Header!'),
          })}
          getColumnProps={column => ({
            onClick: () => alert('Column! +' + JSON.stringify(column)),
            style: {
              background: column.index === 1 ? 'white' : 'rgba(0,0,0,0)', // 0.1
            },
          })}
          getRowProps={row => ({
            style: {
              background: row.index % 2 === 0 ? 'rgba(0,0,0,0)' : 'white', // 0.1
            },
          })}
          getCellProps={cellInfo => {
            const modes = cellInfo.row.original.modes;
            const holdStates = cellInfo.row.original.holdStates;
            const rowLabel = cellInfo.row.original.rValue;
            if (!modes) return {};

            const mode = modes[cellInfo.column.id];
            const holdState = holdStates ? holdStates[cellInfo.column.id] : 0;

            // If hold_state=1 (uncertainty), override to yellow regardless of mode
            const displayMode = holdState === 1 ? 'hold' : mode;

            if (!displayMode) return {};
            const colors = { buy: '#4CAF50', sell: '#ff4444', hold: '#ffcc00' };

            // Use gray text for all R 4hr and R Daily row cells for better readability
            const isRRow = rowLabel === 'R 4hr' || rowLabel === 'R Daily';
            const textColor = isRRow ? '#333333' : 'inherit';

            return {
              style: {
                backgroundColor: colors[displayMode] || 'rgba(0,0,0,0)',
                color: textColor
              }
            };
          }}
        />
      </div>
    </div>
  )
}

export default XYR
